'use client'

import { useQuery } from '@apollo/client'
import { ConfigProvider } from 'antd'
import arEG from 'antd/locale/ar_EG'
import enUS from 'antd/locale/en_US'
import frFR from 'antd/locale/fr_FR'
import itIT from 'antd/locale/it_IT'
import nlNL from 'antd/locale/nl_NL'
import elGR from 'antd/locale/el_GR'
import kuIQ from 'antd/locale/ku_IQ'
import plPL from 'antd/locale/pl_PL'
import 'moment/locale/ar'
import 'moment/locale/de'
import 'moment/locale/fr'
import 'moment/locale/it'
import 'moment/locale/el'
import 'moment/locale/nl'
import 'moment/locale/ku'
import 'moment/locale/pl'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import DashboardLoader from '../components/common/DashboardLoader'
import Error from '../components/common/Error'
import { useUser } from '../context/userContext'
import { GlobalStyle } from '../theme'
import * as Sentry from '@sentry/react'
import MainRouter from './MainRouter'
import { CURRENT_USER_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/userQueries'
import getUserPermissions from '@dentolize/clinic-mobile/src/shared/utils/getUserPermissions'
import ReactGA from 'react-ga4'
import { analyticsPage, analyticsPageFields } from '../utils/analyticsPage'
import { useChart } from '../context/chartContext'
import { isMedicolize } from '../variables'
import { datePickerArabicShortDaysFix } from '../shared/utils/datePickers/dateHelpers'
import { FeatureFlagsProvider } from '@dentolize/clinic-web-canary'
import { initDateTime } from '../utils/timezone/time'
import moment from 'moment'

const antProviders = {
  ar: arEG,
  fr: frFR,
  it: itIT,
  nl: nlNL,
  en: enUS,
  el: elGR,
  ku: kuIQ,
  ckb: kuIQ,
  pl: plPL
}

const MainWrapper = () => {
  const { user, setUser } = useUser()
  const { setChart } = useChart()
  const { i18n } = useTranslation()
  const location = useLocation()

  const language = user ? user.language : localStorage.getItem('language') || 'en'
  const rtl = i18n.dir() === 'rtl'

  const { loading, error, data } = useQuery(CURRENT_USER_QUERY)

  const handleChangeLang = useCallback(async () => {
    await i18n.changeLanguage(language)
    if (['ar', 'de', 'fr', 'it', 'el', 'nl', 'pl', 'ku'].includes(language)) {
      moment.updateLocale(language, {
        postformat: function(string) {
          return string // prevents Arabic digit conversion
        }
      })
    } else {
      moment.locale('en')
    }
    document.documentElement.lang = language
    document.documentElement.dir = i18n.dir() === 'rtl' ? 'rtl' : 'ltr'
  }, [i18n, language])

  useEffect(() => {
    window.scrollTo(0, 0)
    ReactGA.set(analyticsPageFields(location.pathname))
    ReactGA.send({ hitType: 'pageview', page: analyticsPage(location.pathname), title: analyticsPage(location.pathname) })
  }, [location])

  useEffect(() => {
    handleChangeLang().then()
  }, [handleChangeLang])

  useEffect(() => {
    if (!loading && data && data.me) {
      const user = {
        ...data.me,
        permissions: getUserPermissions(data.me),
        company: {
          ...data.me.company,
          extraSettings: data.me.company.extraSettings ? JSON.parse(data.me.company.extraSettings) : {}
        }
      }
      if (process.env.NODE_ENV === 'production') {
        Sentry.setUser({ id: user.id, email: user.company.id })
      }
      initDateTime(data.me.company.timeZone)
      sessionStorage.setItem('userId', user.id)
      setChart(data.me.chart)
      setUser(user)
    }
  }, [data, loading])

  if (loading && !data) return <DashboardLoader />
  if (error && !user) return <Error error={error} errorStatus="server" />

  return (
    <ConfigProvider
      locale={{
        ...antProviders[language],
        DatePicker: datePickerArabicShortDaysFix(language)
      }}
      direction={rtl ? 'rtl' : null}
      theme={{
        token: {
          colorPrimary: isMedicolize ? '#653e77' : '#0077b9',
          colorLinkHover: isMedicolize ? '#835996' : '#69b1ff',
          controlItemBgActive: isMedicolize ? '#f8eeff' : '#e1f5fa',
          colorLinkActive: isMedicolize ? '#552a69' : '#0958d9'
        }
      }}
    >
      <FeatureFlagsProvider tenantId={user?.company?.id}>
        <GlobalStyle rtl={rtl} medicolize={isMedicolize} />
        <MainRouter />
      </FeatureFlagsProvider>
    </ConfigProvider>
  )
}

export default MainWrapper
